import { apiClient, ApiError } from '../client';
import { API_CONFIG } from '../config';

// Meta API endpoints (these are different from the main API)
const BASE_URL = `${API_CONFIG.BASE_URL}/meta`;

export interface ExchangeTokenData {
  code: string;
  state?: string;
}

export interface CreateBusinessAccountData {
  businessAccountId: string;
  accessToken: string;
  name: string;
  timezoneId?: string;
}

export interface UpdateAccessTokenData {
  businessAccountId: string;
  accessToken: string;
}

export interface GetTemplatesParams {
  businessAccountId: string;
  limit?: number;
  offset?: number;
}

export interface UploadMediaData {
  businessAccountId: string;
  file: File;
  type?: string;
}

export interface UploadMediaUrlData {
  businessAccountId: string;
  url: string;
  type?: string;
  filename?: string;
}

export interface BusinessProfileUpdatePayload {
  about?: string;
  description?: string;
  email?: string;
  address?: string;
  vertical?: string;
  websites?: string[];
  profile_picture_handle?: string;
}

export interface UploadBusinessProfileLogoResponse {
  success: boolean;
  handle?: string;
  data?: {
    handle?: string;
    url?: string;
  };
  error?: string;
}

export interface MessagingLimitInfo {
  tier: string | null;
  limit: number | null;
  isUnlimited: boolean;
  used: number;
  remaining: number | null;
  windowStart: string | null;
  quality_rating?: string | null;
}

export interface MessagingUsagePhoneNumber {
  id: string;
  display_phone_number: string;
  status?: string | null;
  verified_name?: string | null;
  messaging_limit_tier?: string | null;
  messaging_limit_info?: MessagingLimitInfo | null;
  quality_rating?: string | null;
}

export interface MessagingUsageSummary {
  businessAccountId: string;
  phoneNumbers: MessagingUsagePhoneNumber[];
  aggregate: MessagingLimitInfo;
}

export const metaApi = {
  /**
   * Get Meta configuration
   */
  async getConfig() {
    const response = await fetch(`${BASE_URL}/get-config`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get Meta configuration');
    }

    return response.json();
  },

  /**
   * Get WhatsApp phone numbers for a stored business account (server uses stored token)
   */
  async getPhoneNumbers(businessAccountRecordId: string) {
    const response = await fetch(`${BASE_URL}/whatsapp/phone-numbers/${businessAccountRecordId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get phone numbers');
    }

    return response.json();
  },

  /**
   * Get messaging limit usage stats for a business account
   */
  async getMessagingUsage(businessAccountRecordId: string) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // BASE_URL already includes /api/v1/meta, so we just append the path
    const response = await fetch(`${BASE_URL}/whatsapp/messaging-usage/${businessAccountRecordId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get messaging usage');
    }

    return response.json();
  },

  /**
   * Exchange authorization code for access token
   */
  async exchangeToken(data: ExchangeTokenData) {
    const response = await fetch(`${BASE_URL}/exchange-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to exchange token');
    }

    return response.json();
  },

  /**
   * Get business accounts
   */
  async getBusinessAccounts(accessToken: string) {
    const response = await fetch(`${BASE_URL}/business-accounts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get business accounts');
    }

    return response.json();
  },

  /**
   * Create business account
   */
  async createBusinessAccount(data: CreateBusinessAccountData) {
    const response = await fetch(`${BASE_URL}/create-business-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create business account');
    }

    return response.json();
  },

  /**
   * Update access token
   */
  async updateAccessToken(data: UpdateAccessTokenData) {
    const response = await fetch(`${BASE_URL}/update-access-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update access token');
    }

    return response.json();
  },

  /**
   * Delink business account from Meta OAuth
   */
  async unlinkBusinessAccount(accountId: string) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/unlink-business-account/${accountId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to delink business account');
    }

    return response.json();
  },

  /**
   * Get templates
   */
  async getTemplates(params: GetTemplatesParams) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    const response = await fetch(`${BASE_URL}/templates?${queryParams.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get templates');
    }

    return response.json();
  },

  /**
   * Get template by ID
   */
  async getTemplate(templateId: string, businessAccountId: string) {
    const response = await fetch(`${BASE_URL}/templates/${templateId}?businessAccountId=${businessAccountId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get template');
    }

    return response.json();
  },


  /**
   * Create template
   */
  async createTemplate(data: any) {
    const response = await fetch(`${BASE_URL}/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create template');
    }

    return response.json();
  },

  /**
   * Delete template
   */
  async deleteTemplate(templateId: string, businessAccountId: string) {
    const response = await fetch(`${BASE_URL}/templates/${templateId}?businessAccountId=${businessAccountId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to delete template');
    }

    return response.json();
  },

  /**
   * Upload media
   */
  async uploadMedia(data: UploadMediaData) {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('businessAccountId', data.businessAccountId);
    if (data.type) {
      formData.append('type', data.type);
    }

    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/media/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload media');
    }

    return response.json();
  },

  /**
   * Upload WhatsApp business profile logo
   */
  async uploadBusinessProfileLogo(accountId: string, file: File) {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('logo', file);

    const response = await fetch(`${BASE_URL}/business-profile/${accountId}/logo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload business profile logo');
    }

    return response.json() as Promise<UploadBusinessProfileLogoResponse>;
  },

  /**
   * Upload media from URL
   * Downloads the media from URL and uploads it to WhatsApp
   */
  async uploadMediaUrl(data: UploadMediaUrlData) {
    // First, download the media from the URL
    const mediaResponse = await fetch(data.url);
    if (!mediaResponse.ok) {
      throw new Error(`Failed to download media from URL: ${mediaResponse.statusText}`);
    }

    const mediaBlob = await mediaResponse.blob();
    const file = new File([mediaBlob], data.filename || 'media', { type: mediaBlob.type });

    // Then upload using the regular media upload endpoint
    return this.uploadMedia({
      file,
      businessAccountId: data.businessAccountId,
      type: data.type
    });
  },

  /**
   * Create resumable upload session
   */
  async createResumableUploadSession(data: {
    businessAccountId: string;
    fileLength: number;
    fileType: string;
    fileName: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/create-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create upload session');
    }

    return response.json();
  },

  /**
   * Upload file data to resumable upload session
   */
  async uploadFileData(data: {
    businessAccountId: string;
    uploadId: string;
    fileData: ArrayBuffer;
    fileOffset?: number;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/upload-data`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/octet-stream',
        'file-offset': (data.fileOffset || 0).toString(),
      },
      body: data.fileData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload file data');
    }

    return response.json();
  },

  /**
   * Query upload status
   */
  async queryUploadStatus(data: {
    businessAccountId: string;
    uploadId: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to query upload status');
    }

    return response.json();
  },

  /**
   * Upload media using resumable upload API and return handle
   */
  async uploadMediaWithResumableAPI(data: {
    businessAccountId: string;
    mediaUrl: string;
    mediaType: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/upload-media`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload media with resumable API');
    }

    return response.json();
  },

  /**
   * Get WhatsApp business profile details
   */
  async getBusinessProfile(accountId: string) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/business-profile/${accountId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to fetch business profile');
    }

    return response.json();
  },

  /**
   * Update WhatsApp business profile details
   */
  async updateBusinessProfile(accountId: string, data: BusinessProfileUpdatePayload) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/business-profile/${accountId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update business profile');
    }

    return response.json();
  },

};
